import { MAX_EMAIL_LENGTH, isPlausibleEmail, normalizeEmail } from './email';

/**
 * The signup decision. Everything about whether a submission becomes a row is decided
 * here, from `(rawEmail, now)` alone — no database, no clock, no network. That is what
 * lets the Worker be a courier and lets these branches be tested as function calls.
 */

export type RejectionReason = 'empty' | 'malformed' | 'too_long';

export interface SignupRecord {
  email: string;
  createdAt: string;
  /** Where the address came from. One entry point today; the column outlives that. */
  source: 'landing';
}

export type SignupDecision =
  | { ok: true; record: SignupRecord }
  | { ok: false; reason: RejectionReason };

/**
 * Coarse guard so a huge input is rejected on its size rather than scanned. Generous
 * enough that it never fires on anything a person typed — the real limit is
 * `MAX_EMAIL_LENGTH`, applied after trimming.
 */
const SCAN_LIMIT = 4 * MAX_EMAIL_LENGTH;

/**
 * `now` is a parameter, not `Date.now()`. That is the difference between a timestamp
 * you can assert on and one you can only approximate.
 *
 * The three rejection reasons are distinct because the page shows different copy for
 * each and the funnel event carries the reason — a spike in `malformed` is a signal
 * about the form, and it is invisible if every rejection looks the same.
 */
export function decideSignup(rawEmail: string, now: string): SignupDecision {
  // Size first, before any scanning: a 100k-character body should be rejected on its
  // length, not walked character by character to discover it has no @.
  if (rawEmail.length > SCAN_LIMIT) return { ok: false, reason: 'too_long' };

  const email = normalizeEmail(rawEmail);
  if (email.length === 0) return { ok: false, reason: 'empty' };

  // Length is measured after trimming, so a pasted address carrying whitespace is
  // judged on the address rather than on the padding.
  if (email.length > MAX_EMAIL_LENGTH) return { ok: false, reason: 'too_long' };
  if (!isPlausibleEmail(email)) return { ok: false, reason: 'malformed' };

  return { ok: true, record: { email, createdAt: now, source: 'landing' } };
}
